import React, { useState, useEffect, useMemo } from 'react';
import { 
  FileText, Download, Eye, Calendar, DollarSign, TrendingUp,
  ChevronLeft, ChevronRight, Printer, CheckCircle2, Clock, 
  XCircle, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Separator } from '../../ui/separator';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { db } from '../../../lib/db';
import { formatCurrency, getPayrollStatusColor, formatPayrollStatus, calculateYTDTotals } from '../../../lib/payrollCalculations';
import { formatDate, getMonthName, getCurrentMonth, getCurrentYear } from '../../../lib/dateUtils';
import { generatePayslipPDF } from '../../../lib/export';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

export default function MyPayslips() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [payslips, setPayslips] = useState([]);
  const [salaryStructure, setSalaryStructure] = useState(null);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const [payslipsData, salaryData] = await Promise.all([
        db.payslips.where('userId').equals(user.id).toArray(),
        db.salaryStructures.where('userId').equals(user.id).first()
      ]);
      setPayslips(payslipsData.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      }));
      setSalaryStructure(salaryData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const yearPayslips = useMemo(() => {
    return payslips.filter(p => p.year === selectedYear);
  }, [payslips, selectedYear]);

  const monthlyTrend = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const payslip = yearPayslips.find(p => p.month === month);
      return {
        month: getMonthName(month).substring(0, 3),
        fullMonth: getMonthName(month),
        net: payslip?.netPay || 0,
        gross: payslip?.grossPay || payslip?.grossEarnings || 0
      };
    });
  }, [yearPayslips]);

  const ytdTotals = useMemo(() => {
    return {
      gross: yearPayslips.reduce((sum, p) => sum + (Number(p.grossPay) || Number(p.grossEarnings) || 0), 0),
      net: yearPayslips.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0),
      deductions: yearPayslips.reduce((sum, p) => sum + (Number(p.totalDeductions) || ((Number(p.totalPreTax) || 0) + (Number(p.totalTaxes) || 0) + (Number(p.totalPostTax) || 0)) || 0), 0),
      federalTax: yearPayslips.reduce((sum, p) => sum + (Number(p.taxes?.federalIncome) || 0), 0),
      socialSecurity: yearPayslips.reduce((sum, p) => sum + (Number(p.taxes?.socialSecurity) || 0), 0),
      medicare: yearPayslips.reduce((sum, p) => sum + (Number(p.taxes?.medicare) || 0), 0),
      stateTax: yearPayslips.reduce((sum, p) => sum + (Number(p.taxes?.stateIncome) || 0), 0),
      totalTaxes: yearPayslips.reduce((sum, p) => sum + (Number(p.totalTaxes) || 0), 0),
      retirement401k: yearPayslips.reduce((sum, p) => sum + (Number(p.preTaxDeductions?.retirement401k) || 0), 0)
    };
  }, [yearPayslips]);

  const latestPayslip = payslips[0];

  const viewPayslip = (payslip) => {
    setSelectedPayslip(payslip);
    setShowPayslipModal(true);
  };

  const downloadPayslip = (payslip) => {
    try {
      generatePayslipPDF(payslip, user);
      toast.success('Payslip PDF downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF');
    }
  };

  const generatePayslipText = (payslip) => {
    const earnings = payslip.earnings || {};
    const taxes = payslip.taxes || {};
    const preTaxDeductions = payslip.preTaxDeductions || {};
    const ytd = payslip.ytd || {};
    
    return `
===============================================
              US PAYROLL PAYSLIP
===============================================
Pay Period: ${getMonthName(payslip.month)} ${payslip.year}
Pay Frequency: ${payslip.payFrequency || 'Bi-Weekly'}
Generated: ${formatDate(payslip.generatedAt)}

EMPLOYEE DETAILS
-----------------------------------------------
Name: ${user?.profile?.firstName} ${user?.profile?.lastName}
Employee ID: ${user?.id?.substring(0, 8)}
Department: ${user?.profile?.department}
Position: ${user?.profile?.position}
Work State: ${payslip.workState || user?.profile?.workState || 'N/A'}

EARNINGS
-----------------------------------------------
Regular Pay:           ${formatCurrency(earnings.regular || payslip.grossPay || 0)}
Overtime:              ${formatCurrency(earnings.overtime || 0)}
Bonus:                 ${formatCurrency(earnings.bonus || 0)}
Commission:            ${formatCurrency(earnings.commission || 0)}
Holiday Pay:           ${formatCurrency(earnings.holiday || 0)}
PTO:                   ${formatCurrency(earnings.pto || 0)}
-----------------------------------------------
GROSS PAY:             ${formatCurrency(payslip.grossPay || payslip.grossEarnings)}

PRE-TAX DEDUCTIONS
-----------------------------------------------
401(k):                ${formatCurrency(preTaxDeductions.retirement401k || 0)}
Health Insurance:      ${formatCurrency(preTaxDeductions.healthInsurance || 0)}
HSA:                   ${formatCurrency(preTaxDeductions.hsa || 0)}
FSA:                   ${formatCurrency(preTaxDeductions.fsa || 0)}
Dental:                ${formatCurrency(preTaxDeductions.dental || 0)}
Vision:                ${formatCurrency(preTaxDeductions.vision || 0)}
-----------------------------------------------
TOTAL PRE-TAX:         ${formatCurrency(payslip.totalPreTax || 0)}

TAXES
-----------------------------------------------
Federal Income Tax:    ${formatCurrency(taxes.federalIncome || 0)}
Social Security:       ${formatCurrency(taxes.socialSecurity || 0)}
Medicare:              ${formatCurrency(taxes.medicare || 0)}
State Tax (${taxes.stateName || payslip.workState || 'N/A'}):       ${formatCurrency(taxes.stateIncome || 0)}
SDI:                   ${formatCurrency(taxes.sdi || 0)}
-----------------------------------------------
TOTAL TAXES:           ${formatCurrency(payslip.totalTaxes || 0)}

===============================================
NET PAY:               ${formatCurrency(payslip.netPay)}
===============================================

YEAR-TO-DATE
-----------------------------------------------
Gross Pay:             ${formatCurrency(ytd.grossPay || 0)}
Federal Tax:           ${formatCurrency(ytd.federalTax || 0)}
Social Security:       ${formatCurrency(ytd.socialSecurity || 0)}
Medicare:              ${formatCurrency(ytd.medicare || 0)}
State Tax:             ${formatCurrency(ytd.stateTax || 0)}
401(k):                ${formatCurrency(ytd.retirement401k || 0)}
Net Pay:               ${formatCurrency(ytd.netPay || 0)}

This is a computer-generated US payroll statement.
    `.trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Payslips</h1>
          <p className="text-gray-500 mt-1">View and download your salary slips</p>
        </div>
      </div>

      {/* Latest Payslip Summary */}
      {latestPayslip && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-teal-500 to-emerald-600 text-white">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-teal-100">Latest Payslip</p>
                <p className="text-2xl font-bold mt-1">
                  {getMonthName(latestPayslip.month)} {latestPayslip.year}
                </p>
              </div>
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <p className="text-teal-100 text-sm">Gross</p>
                  <p className="text-xl font-bold">{formatCurrency(latestPayslip.grossPay || latestPayslip.grossEarnings)}</p>
                </div>
                <div className="text-center">
                  <p className="text-teal-100 text-sm">Deductions</p>
                  <p className="text-xl font-bold">-{formatCurrency(latestPayslip.totalDeductions || ((latestPayslip.totalPreTax || 0) + (latestPayslip.totalTaxes || 0) + (latestPayslip.totalPostTax || 0)))}</p>
                </div>
                <div className="text-center">
                  <p className="text-teal-100 text-sm">Net Pay</p>
                  <p className="text-3xl font-bold">{formatCurrency(latestPayslip.netPay)}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="bg-white/10 border-white/20 hover:bg-white/20 text-white"
                  onClick={() => viewPayslip(latestPayslip)}
                >
                  <Eye className="w-4 h-4 mr-2" /> View
                </Button>
                <Button 
                  variant="outline" 
                  className="bg-white/10 border-white/20 hover:bg-white/20 text-white"
                  onClick={() => downloadPayslip(latestPayslip)}
                >
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Year Selector and YTD Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle>Salary Trend</CardTitle>
              <CardDescription>Your monthly earnings this year</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setSelectedYear(selectedYear - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-bold">{selectedYear}</span>
              <Button variant="ghost" size="icon" onClick={() => setSelectedYear(selectedYear + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => monthlyTrend.find(d => d.month === label)?.fullMonth}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="net" 
                    stroke="#14b8a6" 
                    fill="#14b8a6" 
                    fillOpacity={0.3}
                    name="Net Salary"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>YTD Summary</CardTitle>
            <CardDescription>Year-to-date totals for {selectedYear}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Gross Earnings</span>
                <span className="font-bold text-gray-900">{formatCurrency(ytdTotals.gross)}</span>
              </div>
            </div>
            <div className="p-3 bg-violet-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">401(k) Contributions</span>
                <span className="font-bold text-violet-600">{formatCurrency(ytdTotals.retirement401k)}</span>
              </div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Taxes (Fed/State/FICA)</span>
                <span className="font-bold text-amber-600">{formatCurrency(ytdTotals.totalTaxes)}</span>
              </div>
            </div>
            <Separator />
            <div className="p-3 bg-emerald-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-900">Net Received</span>
                <span className="text-xl font-bold text-emerald-600">{formatCurrency(ytdTotals.net)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* All Payslips */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>All Payslips</CardTitle>
          <CardDescription>Complete history of your payslips</CardDescription>
        </CardHeader>
        <CardContent>
          {payslips.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p>No payslips available yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {payslips.map(payslip => (
                <div 
                  key={payslip.id}
                  className="p-4 border border-gray-100 rounded-xl hover:border-teal-200 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => viewPayslip(payslip)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{getMonthName(payslip.month)}</p>
                        <p className="text-xs text-gray-500">{payslip.year}</p>
                      </div>
                    </div>
                    <Badge className={getPayrollStatusColor(payslip.status)}>
                      {formatPayrollStatus(payslip.status)}
                    </Badge>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Gross</span>
                      <span className="font-medium">{formatCurrency(payslip.grossPay || payslip.grossEarnings)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Deductions</span>
                      <span className="text-red-500">-{formatCurrency(payslip.totalDeductions || ((payslip.totalPreTax || 0) + (payslip.totalTaxes || 0) + (payslip.totalPostTax || 0)))}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between pt-1">
                      <span className="font-medium text-gray-900">Net Pay</span>
                      <span className="font-bold text-emerald-600">{formatCurrency(payslip.netPay)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payslip Detail Modal */}
      <Dialog open={showPayslipModal} onOpenChange={setShowPayslipModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Payslip
            </DialogTitle>
            <DialogDescription>
              {selectedPayslip && `${getMonthName(selectedPayslip.month)} ${selectedPayslip.year}`}
            </DialogDescription>
          </DialogHeader>

          {selectedPayslip && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-lg font-medium">
                  {user?.profile?.firstName?.[0]}{user?.profile?.lastName?.[0]}
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    {user?.profile?.firstName} {user?.profile?.lastName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {user?.profile?.department} • {user?.profile?.position}
                  </p>
                  {selectedPayslip.workState && (
                    <p className="text-xs text-gray-400">Work State: {selectedPayslip.workState}</p>
                  )}
                </div>
                <div className="ml-auto">
                  <Badge className={getPayrollStatusColor(selectedPayslip.status)}>
                    {formatPayrollStatus(selectedPayslip.status)}
                  </Badge>
                </div>
              </div>

              {/* Earnings */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  Earnings
                </h3>
                <div className="space-y-2 p-4 bg-emerald-50 rounded-lg">
                  {selectedPayslip.earnings?.regular > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Regular Pay</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.regular)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.overtime > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Overtime</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.overtime)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.bonus > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Bonus</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.bonus)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.commission > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Commission</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.commission)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.holiday > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Holiday Pay</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.holiday)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.pto > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">PTO</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.pto)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Gross Pay</span>
                    <span className="text-emerald-600">{formatCurrency(selectedPayslip.grossPay || selectedPayslip.grossEarnings)}</span>
                  </div>
                </div>
              </div>

              {/* Pre-Tax Deductions */}
              {selectedPayslip.preTaxDeductions && Object.values(selectedPayslip.preTaxDeductions).some(v => v > 0) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-violet-600" />
                    Pre-Tax Deductions
                  </h3>
                  <div className="space-y-2 p-4 bg-violet-50 rounded-lg">
                    {selectedPayslip.preTaxDeductions?.retirement401k > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">401(k)</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.retirement401k)}</span>
                      </div>
                    )}
                    {selectedPayslip.preTaxDeductions?.healthInsurance > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Health Insurance</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.healthInsurance)}</span>
                      </div>
                    )}
                    {selectedPayslip.preTaxDeductions?.hsa > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">HSA</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.hsa)}</span>
                      </div>
                    )}
                    {selectedPayslip.preTaxDeductions?.dental > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Dental</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.dental)}</span>
                      </div>
                    )}
                    <Separator />
                    <div className="flex justify-between font-semibold">
                      <span>Total Pre-Tax</span>
                      <span className="text-violet-600">-{formatCurrency(selectedPayslip.totalPreTax)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Taxes (US Style) */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  Taxes
                </h3>
                <div className="space-y-2 p-4 bg-red-50 rounded-lg">
                  {selectedPayslip.taxes?.federalIncome > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Federal Income Tax</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.federalIncome)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.socialSecurity > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Social Security (6.2%)</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.socialSecurity)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.medicare > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Medicare (1.45%)</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.medicare)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.stateIncome > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">State Tax ({selectedPayslip.taxes.stateName || selectedPayslip.workState})</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.stateIncome)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.sdi > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">SDI</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.sdi)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.localTax > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Local Tax</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.localTax)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Total Taxes</span>
                    <span className="text-red-600">-{formatCurrency(selectedPayslip.totalTaxes)}</span>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="p-4 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-lg text-white">
                <div className="flex justify-between items-center">
                  <span className="text-lg">Net Pay</span>
                  <span className="text-3xl font-bold">{formatCurrency(selectedPayslip.netPay)}</span>
                </div>
              </div>

              {/* YTD Summary */}
              {selectedPayslip.ytd && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Year-to-Date</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Gross</span>
                      <span className="font-medium">{formatCurrency(selectedPayslip.ytd.grossPay)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Net</span>
                      <span className="font-medium">{formatCurrency(selectedPayslip.ytd.netPay)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Fed Tax</span>
                      <span className="font-medium">{formatCurrency(selectedPayslip.ytd.federalTax)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">SS + Medicare</span>
                      <span className="font-medium">{formatCurrency((selectedPayslip.ytd.socialSecurity || 0) + (selectedPayslip.ytd.medicare || 0))}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Meta Info */}
              <div className="text-sm text-gray-500 text-center">
                Generated on {formatDate(selectedPayslip.generatedAt)}
                {selectedPayslip.w4Info && (
                  <span className="ml-2">• Filing Status: {selectedPayslip.w4Info.filingStatus}</span>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayslipModal(false)}>Close</Button>
            <Button 
              onClick={() => downloadPayslip(selectedPayslip)}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
